import {
  type CSSProperties,
  forwardRef,
  memo,
  useEffect,
  useState,
} from 'react'
import { Input, Divider } from 'antd'
import type { TextAreaProps, TextAreaRef } from 'antd/es/input/TextArea'
import { useSnapshot } from 'valtio'
import { X, LayoutPanelTop } from 'lucide-react'

import { chatState, chatAction, configState } from '@/store'
import { useStyles } from './style'
import useChineseInput from '@/hooks/useChineseInput'
import DragUpload from '@/components/DragUpload'
import useDragUpload from '@/hooks/useDragUpload'
import FileItem from '@/components/FileItems'
import { SendBtn } from './FooterAddition'
import ImageUpload from './actions/ImageUpload'
import StarSvg from '@/assets/images/icon/hot-left-title.svg?react'
import CaretDown from '@/assets/images/icon/caret-down.svg?react'
import { TemplateCardList } from './TemplateCardList'

export interface ChatInputAreaProps extends Omit<TextAreaProps, 'classNames'> {
  onSend: () => void
  onStop: () => void
  loading?: boolean
  className?: string
  style?: CSSProperties
}

const InteractiveChatInputArea = forwardRef<TextAreaRef, ChatInputAreaProps>(
  ({ onSend, onStop, loading, className, style }, ref) => {
    const { inputText, inputFileList, sessionMessages } = useSnapshot(chatState)
    const { baseConfig } = useSnapshot(configState)
    const { isDragging, handlePaste } = useDragUpload()
    const [showTemplate, setShowTemplate] = useState(false)

    const { cx, styles } = useStyles()
    const {
      curValue,
      handleChange,
      handleCompositionStart,
      handleCompositionEnd,
    } = useChineseInput(inputText, (value: string) => {
      chatAction.setInputText(value)
    })

    useEffect(() => {
      if (chatState.sessionMessages.length > 0 || curValue.trim() !== '') {
        setShowTemplate(false)
      } else {
        setShowTemplate(true)
      }
    }, [sessionMessages.length, curValue])

    return (
      <section className={cx(styles.chatInputArea, className)} style={style}>
        <div className="flex justify-between px-5">
          <div className="flex gap-1 justify-center items-center">
            <StarSvg className="size-4" />
            交互思考
          </div>
          <div className="flex items-center text-content-second">
            <div
              className="flex items-center gap-1 cursor-pointer text-content-second"
              onClick={() => {
                setShowTemplate(!showTemplate)
              }}
            >
              <LayoutPanelTop className="size-4" />
              <span>模版</span>
              <CaretDown
                className={`size-3 ${showTemplate ? 'rotate-180' : ''}`}
              />
            </div>
            <Divider type="vertical" />
            <X
              className="size-4 cursor-pointer hover:text-content-third"
              strokeWidth={1.2}
              onClick={() => {
                chatState.interactiveMode = false
              }}
            />
          </div>
        </div>
        <div
          className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
            showTemplate ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          }`}
        >
          <TemplateCardList />
        </div>
        <DragUpload isDragging={isDragging} />
        {inputFileList.length > 0 && (
          <div className="flex flex-wrap gap-3 px-3">
            {inputFileList.map(file => (
              <FileItem
                key={file.uid}
                file={file}
                onClose={() => {
                  chatState.inputFileList = chatState.inputFileList.filter(
                    i => i.uid !== file.uid,
                  )
                }}
              />
            ))}
          </div>
        )}
        <div className={styles.textAreaWrap}>
          <Input.TextArea
            id="chatInput"
            autoFocus
            ref={ref}
            value={curValue}
            maxLength={baseConfig.limitLength}
            placeholder="请输入您的问题..."
            autoSize={{ maxRows: 3, minRows: 1 }}
            onChange={handleChange}
            onPaste={handlePaste}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            onPressEnter={(e: any) => {
              if (e.key === 'Enter' && e.shiftKey) return
              if (e.keyCode !== 13 || e.isComposing) return
              e.preventDefault()
              onSend()
            }}
          />
        </div>
        <div className={styles.footerAddition}>
          <div />
          <div className="flex items-center justify-center">
            <div className="justify-between items-center text-xs hidden md:flex">
              <div className="flex flex-1 items-center gap-2">
                <ImageUpload />
              </div>
            </div>
            <SendBtn
              curValue={curValue}
              loading={loading}
              onSend={onSend}
              onStop={onStop}
            />
          </div>
        </div>
      </section>
    )
  },
)

export default memo(InteractiveChatInputArea)
